/**
 * Compare tray store — localStorage-backed pin list, subscribed via
 * useSyncExternalStore so any pin toggle instantly rerenders every
 * consumer (tray, buttons, modal).
 *
 * Each pinned item: { kind, slug, name, meta:{...} }
 * kind ∈ 'lender' | 'situation' | 'calculator' | 'city' | 'vehicle'
 */
import { useCallback, useSyncExternalStore } from "react";

const STORAGE_KEY = "rmc_compare_pins";
const MAX_PINS = 4;

const listeners = new Set();
let cache = null;

function readFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function emit() {
  cache = readFromStorage();
  listeners.forEach((fn) => fn());
}

function writeToStorage(pins) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pins));
  } catch {
    /* silently skip if storage is full/blocked */
  }
  emit();
}

// Sync across tabs.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_KEY) emit();
  });
}

function getSnapshot() {
  if (cache === null) cache = readFromStorage();
  return cache;
}

function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function isPinned(kind, slug) {
  return getSnapshot().some((p) => p.kind === kind && p.slug === slug);
}

export function togglePin(item) {
  const cur = getSnapshot();
  const idx = cur.findIndex((p) => p.kind === item.kind && p.slug === item.slug);
  if (idx >= 0) {
    const next = cur.slice();
    next.splice(idx, 1);
    writeToStorage(next);
    return { pinned: false };
  }
  if (cur.length >= MAX_PINS) {
    writeToStorage([...cur.slice(1), item]);
    return { pinned: true, replaced: true };
  }
  writeToStorage([...cur, item]);
  return { pinned: true };
}

export function clearPins() {
  writeToStorage([]);
}

export function usePins() {
  return useSyncExternalStore(subscribe, getSnapshot, () => []);
}

export function usePinToggle() {
  return useCallback((item) => togglePin(item), []);
}
